import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Users, Package, CreditCard, Settings, BarChart3, Search, 
  Ban, CheckCircle, Trash2, Star, Eye, Shield, AlertTriangle,
  DollarSign, TrendingUp, UserPlus, ShoppingBag, Lock, Key
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getStatusStyle = (status) => {
  if (status === 'active' || status === 'paid' || status === 'completed') return 'bg-green-100 text-green-800';
  if (status === 'pending' || status === 'suspended') return 'bg-yellow-100 text-yellow-800';
  if (status === 'approved') return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-800';
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Filters
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [itemSearch, setItemSearch] = useState('');
  const [rentalStatusFilter, setRentalStatusFilter] = useState('all');
  
  // Password change
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Dialogs
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: '', name: '' });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isAuthenticated, user, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, itemsRes, rentalsRes, transRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/items`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/rentals`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/transactions`, { headers: getAuthHeaders() })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setItems(itemsRes.data.items);
      setRentals(rentalsRes.data.rentals);
      setTransactions(transRes.data.transactions);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateUserStatus = async (userId, status) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status?status=${status}`, {}, { headers: getAuthHeaders() });
      toast.success(`User ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeaders() });
      toast.success('User deleted');
      setDeleteDialog({ open: false, type: '', id: '', name: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/admin/items/${itemId}`, { headers: getAuthHeaders() });
      toast.success('Item deleted');
      setDeleteDialog({ open: false, type: '', id: '', name: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const toggleFeatured = async (itemId, featured) => {
    try {
      await axios.put(`${API}/admin/items/${itemId}/feature?featured=${featured}`, {}, { headers: getAuthHeaders() });
      toast.success(featured ? 'Item featured' : 'Item unfeatured');
      fetchData();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const updateRentalStatus = async (rentalId, status) => {
    try {
      await axios.put(`${API}/admin/rentals/${rentalId}/status?status=${status}`, {}, { headers: getAuthHeaders() });
      toast.success('Rental status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update rental');
    }
  };

  const changePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, { headers: getAuthHeaders() });
      
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.full_name.toLowerCase().includes(userSearch.toLowerCase()) || 
                         u.email.toLowerCase().includes(userSearch.toLowerCase());
    const matchesStatus = userStatusFilter === 'all' || u.status === userStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredItems = items.filter(i => 
    i.title.toLowerCase().includes(itemSearch.toLowerCase())
  );

  const filteredRentals = rentals.filter(r => 
    rentalStatusFilter === 'all' || r.status === rentalStatusFilter
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-[#596652]">Loading admin panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-[#2C3329] flex items-center gap-2">
              <Shield className="w-8 h-8 text-[#D97757]" />
              Admin Dashboard
            </h1>
            <p className="text-[#596652]">Manage your platform</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-[#EBE8DF] rounded-xl p-1 mb-6 flex-wrap">
            <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-[#D97757] data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-[#D97757] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="items" className="rounded-lg data-[state=active]:bg-[#D97757] data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" />
              Items ({items.length})
            </TabsTrigger>
            <TabsTrigger value="rentals" className="rounded-lg data-[state=active]:bg-[#D97757] data-[state=active]:text-white">
              <CreditCard className="w-4 h-4 mr-2" />
              Rentals ({rentals.length})
            </TabsTrigger>
            <TabsTrigger value="transactions" className="rounded-lg data-[state=active]:bg-[#D97757] data-[state=active]:text-white">
              <DollarSign className="w-4 h-4 mr-2" />
              Transactions
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg data-[state=active]:bg-[#D97757] data-[state=active]:text-white">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#596652] text-sm">Total Users</p>
                    <p className="text-3xl font-heading font-semibold text-[#2C3329]">{stats?.total_users || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#8C9A76]/15 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#8C9A76]" />
                  </div>
                </div>
                <p className="text-xs text-[#8C9A76] mt-2">+{stats?.new_users_today || 0} today</p>
              </div>
              
              <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#596652] text-sm">Total Items</p>
                    <p className="text-3xl font-heading font-semibold text-[#2C3329]">{stats?.total_items || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#D97757]/15 rounded-full flex items-center justify-center">
                    <Package className="w-6 h-6 text-[#D97757]" />
                  </div>
                </div>
                <p className="text-xs text-[#D97757] mt-2">+{stats?.new_items_today || 0} today</p>
              </div>
              
              <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#596652] text-sm">Active Rentals</p>
                    <p className="text-3xl font-heading font-semibold text-[#2C3329]">{stats?.active_rentals || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#F59E0B]/15 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-[#F59E0B]" />
                  </div>
                </div>
                <p className="text-xs text-[#F59E0B] mt-2">{stats?.pending_rentals || 0} pending</p>
              </div>
              
              <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#596652] text-sm">Total Revenue</p>
                    <p className="text-3xl font-heading font-semibold text-[#2C3329]">${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#4CAF50]/15 rounded-full flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-[#4CAF50]" />
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Quick Actions - Change Password */}
              <div className="bg-gradient-to-br from-[#D97757] to-[#C56547] rounded-2xl p-6 text-white">
                <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Quick Actions
                </h3>
                <div className="space-y-3">
                  <Button 
                    onClick={() => setActiveTab('settings')}
                    className="w-full bg-white/20 hover:bg-white/30 text-white border-0 rounded-xl"
                    data-testid="quick-change-password"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                  <p className="text-white/70 text-sm">
                    Secure your admin account by updating your password regularly.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
                <h3 className="font-heading font-semibold text-lg text-[#2C3329] mb-4">Recent Users</h3>
                <div className="space-y-3">
                  {users.slice(0, 5).map(u => (
                    <div key={u.id} className="flex items-center justify-between py-2 border-b border-[#EBE8DF] last:border-0">
                      <div>
                        <p className="font-medium text-[#2C3329]">{u.full_name}</p>
                        <p className="text-sm text-[#596652]">{u.email}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        u.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>{u.status || 'active'}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
                <h3 className="font-heading font-semibold text-lg text-[#2C3329] mb-4">Recent Rentals</h3>
                <div className="space-y-3">
                  {rentals.slice(0, 5).map(r => (
                    <div key={r.id} className="flex items-center justify-between py-2 border-b border-[#EBE8DF] last:border-0">
                      <div>
                        <p className="font-medium text-[#2C3329]">{r.item_title}</p>
                        <p className="text-sm text-[#596652]">${r.total_price}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusStyle(r.status)}`}>{r.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#596652]" />
                  <Input
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-10 border-[#EBE8DF] rounded-xl"
                  />
                </div>
                <Select value={userStatusFilter} onValueChange={setUserStatusFilter}>
                  <SelectTrigger className="w-40 border-[#EBE8DF] rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="banned">Banned</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#EBE8DF]">
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">User</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Location</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Items</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Rating</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Status</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u.id} className="border-b border-[#EBE8DF] hover:bg-[#FDFBF7]">
                        <td className="py-3 px-2">
                          <div>
                            <p className="font-medium text-[#2C3329]">{u.full_name}</p>
                            <p className="text-sm text-[#596652]">{u.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-[#596652]">
                          {u.neighborhood}
                          {u.zipcode && <span className="text-xs ml-1">({u.zipcode})</span>}
                        </td>
                        <td className="py-3 px-2 text-[#596652]">{u.items_count || 0}</td>
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-[#F59E0B] fill-current" />
                            <span>{u.rating?.toFixed(1) || '0.0'}</span>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusStyle(u.status || 'active')}`}>{u.status || 'active'}</span>
                          {u.role === 'admin' && (
                            <span className="ml-1 px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">Admin</span>
                          )}
                        </td>
                        <td className="py-3 px-2">
                          {u.role !== 'admin' && (
                            <div className="flex gap-1">
                              {u.status !== 'suspended' ? (
                                <Button size="sm" variant="ghost" onClick={() => updateUserStatus(u.id, 'suspended')} className="text-yellow-600 hover:bg-yellow-50">
                                  <Ban className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button size="sm" variant="ghost" onClick={() => updateUserStatus(u.id, 'active')} className="text-green-600 hover:bg-green-50">
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => setDeleteDialog({ open: true, type: 'user', id: u.id, name: u.full_name })} className="text-red-600 hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Items Tab */}
          <TabsContent value="items">
            <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
              <div className="flex gap-4 mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#596652]" />
                  <Input
                    placeholder="Search items..."
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                    className="pl-10 border-[#EBE8DF] rounded-xl"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#EBE8DF]">
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Item</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Owner</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Category</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Price/Day</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Status</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map(item => (
                      <tr key={item.id} className="border-b border-[#EBE8DF] hover:bg-[#FDFBF7]">
                        <td className="py-3 px-2">
                          <p className="font-medium text-[#2C3329]">{item.title}</p>
                        </td>
                        <td className="py-3 px-2 text-[#596652]">{item.owner_name}</td>
                        <td className="py-3 px-2">
                          <span className="px-2 py-1 rounded-full text-xs bg-[#8C9A76]/15 text-[#596652]">{item.category}</span>
                        </td>
                        <td className="py-3 px-2 text-[#D97757] font-medium">${item.price_daily}</td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            item.available ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>{item.available ? 'Available' : 'Rented'}</span>
                          {item.featured && (
                            <span className="ml-1 px-2 py-1 rounded-full text-xs bg-[#D97757]/15 text-[#D97757]">Featured</span>
                          )}
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => window.open(`/items/${item.id}`, '_blank')} className="text-[#596652]">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => toggleFeatured(item.id, !item.featured)} className={item.featured ? 'text-[#D97757]' : 'text-[#596652]'}>
                              <Star className={`w-4 h-4 ${item.featured ? 'fill-current' : ''}`} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setDeleteDialog({ open: true, type: 'item', id: item.id, name: item.title })} className="text-red-600 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Rentals Tab */}
          <TabsContent value="rentals">
            <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
              <div className="flex gap-4 mb-6">
                <Select value={rentalStatusFilter} onValueChange={setRentalStatusFilter}>
                  <SelectTrigger className="w-40 border-[#EBE8DF] rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#EBE8DF]">
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Item</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Renter</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Owner</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Price</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Status</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRentals.map(r => (
                      <tr key={r.id} className="border-b border-[#EBE8DF] hover:bg-[#FDFBF7]">
                        <td className="py-3 px-2 font-medium text-[#2C3329]">{r.item_title}</td>
                        <td className="py-3 px-2 text-[#596652]">{r.renter_name}</td>
                        <td className="py-3 px-2 text-[#596652]">{r.owner_name}</td>
                        <td className="py-3 px-2 text-[#D97757] font-medium">${r.total_price}</td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            r.status === 'paid' || r.status === 'completed' ? 'bg-green-100 text-green-800' :
                            r.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            r.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>{r.status}</span>
                        </td>
                        <td className="py-3 px-2">
                          <Select onValueChange={(v) => updateRentalStatus(r.id, v)}>
                            <SelectTrigger className="w-32 h-8 border-[#EBE8DF] rounded-lg text-xs">
                              <SelectValue placeholder="Change" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="approved">Approve</SelectItem>
                              <SelectItem value="paid">Mark Paid</SelectItem>
                              <SelectItem value="completed">Complete</SelectItem>
                              <SelectItem value="cancelled">Cancel</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#EBE8DF]">
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Transaction ID</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Rental</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Amount</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Status</th>
                      <th className="text-left py-3 px-2 text-[#596652] font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(t => (
                      <tr key={t.id} className="border-b border-[#EBE8DF] hover:bg-[#FDFBF7]">
                        <td className="py-3 px-2 text-[#596652] font-mono text-sm">{t.id.slice(0, 8)}...</td>
                        <td className="py-3 px-2 text-[#596652]">{t.rental_id?.slice(0, 8)}...</td>
                        <td className="py-3 px-2 text-[#D97757] font-medium">${t.amount}</td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            t.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                            t.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>{t.payment_status}</span>
                        </td>
                        <td className="py-3 px-2 text-[#596652] text-sm">
                          {new Date(t.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Change Password */}
              <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
                <h3 className="font-heading font-semibold text-lg text-[#2C3329] mb-4 flex items-center gap-2">
                  <Key className="w-5 h-5 text-[#D97757]" />
                  Change Password
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="currentPassword" className="text-[#2C3329]">Current Password</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      className="mt-1 border-[#EBE8DF] rounded-xl"
                      placeholder="Enter current password"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="newPassword" className="text-[#2C3329]">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      className="mt-1 border-[#EBE8DF] rounded-xl"
                      placeholder="Enter new password (min 6 characters)"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword" className="text-[#2C3329]">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      className="mt-1 border-[#EBE8DF] rounded-xl"
                      placeholder="Confirm new password"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <Button
                    onClick={changePassword}
                    disabled={changingPassword || !passwordData.currentPassword || !passwordData.newPassword}
                    className="w-full bg-[#D97757] text-white rounded-full hover:bg-[#C56547]"
                    data-testid="change-password-button"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    {changingPassword ? 'Changing...' : 'Change Password'}
                  </Button>
                </div>
              </div>

              {/* Admin Info */}
              <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
                <h3 className="font-heading font-semibold text-lg text-[#2C3329] mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#8C9A76]" />
                  Admin Account Info
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-[#EBE8DF]">
                    <span className="text-[#596652]">Email</span>
                    <span className="font-medium text-[#2C3329]">{user?.email}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#EBE8DF]">
                    <span className="text-[#596652]">Name</span>
                    <span className="font-medium text-[#2C3329]">{user?.full_name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#EBE8DF]">
                    <span className="text-[#596652]">Role</span>
                    <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">Admin</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-[#596652]">Status</span>
                    <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">Active</span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: '', id: '', name: '' })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Confirm Delete
              </DialogTitle>
            </DialogHeader>
            <p className="text-[#596652]">
              Are you sure you want to delete <strong>{deleteDialog.name}</strong>? This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: '', id: '', name: '' })}>
                Cancel
              </Button>
              <Button 
                onClick={() => deleteDialog.type === 'user' ? deleteUser(deleteDialog.id) : deleteItem(deleteDialog.id)}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPage;
